// Libs
import classNames from 'classnames/bind';
import { Flex, Heading, Table, Text, TextInput, View } from '@instructure/ui';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
// Components, Layouts, Pages
import { BasePagination, Loading } from '~/components/common';
// Others
import { ExamStatusEnum } from '~/utils/enum/base';
import { useAppDispatch, useAppSelector } from '~/utils/redux/hooks';
import { IQueryGetExams } from '~/utils/interface/exam';
import { getListExams } from '~/thunks/exam/examThunk';
import { DEFAULT_LIMIT_PAGE, DEFAULT_PAGE } from '~/utils/constants/common';
import { useDebounce } from '~/utils/hooks/base';
// Styles, images, icons
import styles from './ExamArea.module.scss';
import { icons } from '~/assets';

const cx = classNames.bind(styles);

const ExamArea = () => {
  //#region Declare Hook
  const { t } = useTranslation(['admin/administrativeArea', 'common/base']);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  //#endregion Declare Hook

  //#region Selector
  const { exams, hasMore } = useAppSelector((state) => state.examState);
  //#endregion Selector

  //#region Declare State
  const [page, setPage] = useState<number>(DEFAULT_PAGE);
  const [searchText, setSearchText] = useState('');
  const [isLoadingOverlay, setIsLoadingOverlay] = useState<boolean>(false);
  const debouncedSearch = useDebounce(searchText);

  //#endregion Declare State

  //#region Implement Hook
  useEffect(() => {
    setPage(DEFAULT_PAGE);
    handleGetExams({ limit: DEFAULT_LIMIT_PAGE, page: DEFAULT_PAGE, textSearch: debouncedSearch.trim() || undefined });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetExams = async (query?: IQueryGetExams) => {
    setIsLoadingOverlay(true);
    await dispatch(
      getListExams(
        query ?? {
          limit: DEFAULT_LIMIT_PAGE,
          page: page,
          textSearch: searchText.trim() || undefined,
        }
      )
    )
      .unwrap()
      .finally(() => {
        setIsLoadingOverlay(false);
      });
  };

  const handleChangePage = (newPage: number) => {
    setPage(newPage);
    handleGetExams({
      limit: DEFAULT_LIMIT_PAGE,
      page: newPage,
      textSearch: searchText.trim() || undefined,
    });
  };

  const handleBack = () => {
    navigate(-1);
  };
  //#endregion Handle Function

  return (
    <div className={cx('viewPort')}>
      <div className={cx('backContainer')} onClick={handleBack}>
        <img src={icons.commonIconBack} alt={t('common_alt_img', { ns: 'common/base' })} className={cx('iconBack')} />
        <Text variant='content'>{t('common_back_button', { ns: 'common/base' })}</Text>
      </div>

      <div className={cx('container')}>
        <View as='div' className={cx('administrativeHeader')} width={'100%'}>
          <View as='div' className={cx('administrativeTitle')}>
            <Heading level='h3'>{t('administrative_area_title', { ns: 'admin/administrativeArea' })}</Heading>
            <img
              className={cx('iconArea')}
              src={icons.commonIconExamArea}
              alt={t('common_alt_img', { ns: 'common/base' })}
            />
          </View>
        </View>

        <Flex justifyItems='end' margin='medium 0 0 0' width='100%'>
          <View as='div' className={cx('searchContainer')}>
            <TextInput
              value={searchText}
              placeholder={t('administrative_area_search_placeholder', { ns: 'admin/administrativeArea' })}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </View>
        </Flex>

        <View className={cx('examTableDesktop')}>
          {exams.length === 0 && !isLoadingOverlay ? (
            <div className={cx('noDataContainer')}>
              <Text variant='contentSmall'>{t('common_no_data_available', { ns: 'common/base' })}</Text>
            </div>
          ) : (
            <>
              <Table layout='auto'>
                <Table.Head>
                  <Table.Row>
                    <Table.ColHeader id='StudentId'>
                      {t('administrative_area_select_student_id_label', { ns: 'admin/administrativeArea' }).replace(
                        ':',
                        ''
                      )}
                    </Table.ColHeader>
                    <Table.ColHeader id='StudentName'>
                      {t('administrative_area_select_student_label', { ns: 'admin/administrativeArea' }).replace(
                        ':',
                        ''
                      )}
                    </Table.ColHeader>
                    <Table.ColHeader id='CourseName'>
                      {t('administrative_area_select_course_label', { ns: 'admin/administrativeArea' }).replace(
                        ':',
                        ''
                      )}
                    </Table.ColHeader>
                    <Table.ColHeader id='Module'>
                      {t('administrative_area_select_module_label', { ns: 'admin/administrativeArea' }).replace(':', '')}
                    </Table.ColHeader>
                    <Table.ColHeader id='PointValue'>
                      {t('administrative_area_table_point_label', { ns: 'admin/administrativeArea' }).replace(':', '')}
                    </Table.ColHeader>
                    <Table.ColHeader id='Value'>
                      {t('administrative_area_table_value_label', { ns: 'admin/administrativeArea' }).replace(':', '')}
                    </Table.ColHeader>
                    <Table.ColHeader id='Status'>
                      {t('administrative_area_table_status_label', { ns: 'admin/administrativeArea' }).replace(':', '')}
                    </Table.ColHeader>
                    <Table.ColHeader id='Credits'>
                      {t('administrative_area_table_credit_label', { ns: 'admin/administrativeArea' }).replace(':', '')}
                    </Table.ColHeader>
                    <Table.ColHeader id='Note'>
                      {t('administrative_area_table_note_label', { ns: 'admin/administrativeArea' }).replace(':', '')}
                    </Table.ColHeader>
                  </Table.Row>
                </Table.Head>
                <Table.Body>
                  {exams.map((data, index) => (
                    <Table.Row key={index}>
                      <Table.Cell>
                        <div className={cx('administrativeData')}>
                          <span>{data.studentId}</span>
                        </div>
                      </Table.Cell>
                      <Table.Cell>
                        <div className={cx('administrativeData')}>
                          <span>{data.studentName}</span>
                        </div>
                      </Table.Cell>

                      <Table.Cell>
                        <div className={cx('administrativeData')}>
                          <span>{data.courseName}</span>
                        </div>
                      </Table.Cell>

                      <Table.Cell>
                        <div className={cx('administrativeData')}>
                          <span>{data.moduleName}</span>
                        </div>
                      </Table.Cell>

                      <Table.Cell>
                        <div className={cx('administrativeData')}>
                          <span>{data.pointValue}</span>
                        </div>
                      </Table.Cell>

                      <Table.Cell>
                        <div className={cx('administrativeData')}>
                          <span>{data.value}</span>
                        </div>
                      </Table.Cell>

                      <Table.Cell>
                        <div className={cx('administrativeStatus')}>
                          <img
                            src={
                              data.status === ExamStatusEnum.FAILED
                                ? icons.commonIconStatusFailed
                                : icons.commonIconChangeStatusActive
                            }
                            alt={t('common_alt_img', { ns: 'common/base' })}
                            className={cx('iconStatus')}
                          />
                          <span>
                            {data.status === ExamStatusEnum.FAILED
                              ? t('administrative_area_failed_status', { ns: 'admin/administrativeArea' })
                              : t('administrative_area_passed_status', { ns: 'admin/administrativeArea' })}
                          </span>
                        </div>
                      </Table.Cell>

                      <Table.Cell>
                        <div className={cx('administrativeData')}>
                          <span>
                            {t('administrative_area_credit_value', {
                              ns: 'admin/administrativeArea',
                              quantity: data.credits,
                            })}
                          </span>
                        </div>
                      </Table.Cell>

                      <Table.Cell>
                        <div className={cx('administrativeData')}>
                          <span>{data.note}</span>
                        </div>
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>

              <BasePagination currentPage={page} hasMore={hasMore} onChange={handleChangePage} />
            </>
          )}
        </View>

        <View className={cx('examTableMobile')}>
          {exams.length === 0 && !isLoadingOverlay && (
            <div className={cx('noDataContainer')}>
              <Text variant='contentSmall'>{t('common_no_data_available', { ns: 'common/base' })}</Text>
            </div>
          )}
          {exams.map((data, index) => (
            <View key={index} as='div' className={cx('card')}>
              <View as='div' className={cx('cardItem')}>
                <span className={cx('cardLabel')}>
                  {t('administrative_area_select_student_id_label', { ns: 'admin/administrativeArea' })}
                </span>
                <span>{data.studentId}</span>
              </View>
              <View as='div' className={cx('cardItem')}>
                <span className={cx('cardLabel')}>
                  {t('administrative_area_select_student_label', { ns: 'admin/administrativeArea' })}
                </span>
                <span>{data.studentName}</span>
              </View>

              <View as='div' className={cx('cardItem')}>
                <span className={cx('cardLabel')}>
                  {t('administrative_area_select_course_label', { ns: 'admin/administrativeArea' })}
                </span>
                <span>{data.courseName}</span>
              </View>

              <View as='div' className={cx('cardItem')}>
                <span className={cx('cardLabel')}>
                  {t('administrative_area_select_module_label', { ns: 'admin/administrativeArea' })}
                </span>
                <span>{data.moduleName}</span>
              </View>

              <View as='div' className={cx('cardItem')}>
                <span className={cx('cardLabel')}>
                  {t('administrative_area_table_point_label', { ns: 'admin/administrativeArea' })}
                </span>
                <span>{data.pointValue}</span>
              </View>

              <View as='div' className={cx('cardItem')}>
                <span className={cx('cardLabel')}>
                  {t('administrative_area_table_value_label', { ns: 'admin/administrativeArea' })}
                </span>
                <span>{data.value}</span>
              </View>

              <View as='div' className={cx('cardItem')}>
                <span className={cx('cardLabel')}>
                  {t('administrative_area_table_credit_label', { ns: 'admin/administrativeArea' })}
                </span>
                <span>
                  {t('administrative_area_credit_value', {
                    ns: 'admin/administrativeArea',
                    quantity: data.credits,
                  })}
                </span>
              </View>

              <View as='div' className={cx('cardItem')}>
                <span className={cx('cardLabel')}>
                  {t('administrative_area_table_status_label', { ns: 'admin/administrativeArea' })}
                </span>

                <View as='div' className={cx('cardItem', 'status')}>
                  <img
                    src={
                      data.status === ExamStatusEnum.FAILED
                        ? icons.commonIconStatusFailed
                        : icons.commonIconChangeStatusActive
                    }
                    alt={t('common_alt_img', { ns: 'common/base' })}
                    className={cx('iconStatus')}
                  />
                  <span>
                    {data.status === ExamStatusEnum.FAILED
                      ? t('administrative_area_failed_status', { ns: 'admin/administrativeArea' })
                      : t('administrative_area_passed_status', { ns: 'admin/administrativeArea' })}
                  </span>
                </View>
              </View>

              {data.note && (
                <View as='div' className={cx('cardItem')}>
                  <span className={cx('cardLabel')}>
                    {t('administrative_area_table_note_label', { ns: 'admin/administrativeArea' })}
                  </span>
                  <span>{data.note}</span>
                </View>
              )}
            </View>
          ))}

          <BasePagination currentPage={page} hasMore={hasMore} onChange={handleChangePage} />
        </View>
      </div>
      <Loading loading={isLoadingOverlay} overlay={true} />
    </div>
  );
};

export default ExamArea;
